import React from "react";
import { Redirect, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import APIComponent from "../../../components/API";
import core from "../../../vendors/core-api";
import { HeaderText } from "../../../components/Base";
import moment from "moment";
import {
  DB_DATE_FORMAT,
  GRAPH_COLORS,
  RESERVATION_DATE_HEADER_FORMAT,
} from "../../../constants";
import { isAccessAllowed } from "../../../auth";
import styled from "styled-components";
import { Col, Row } from "react-bootstrap";
import Chart from "react-apexcharts";
import BWSection from "../../../components/widgets/BWSection";
import PacmanLoading from "../../../components/widgets/PacmanLoading/PacmanLoading";
import { Link } from "react-router-dom";
import { groupBy, isEmpty } from "lodash";
import { toPesoAmount } from "../../../helpers";
import PacmanWrapper from "../../../components/dashboard/PacmanWrapper";
import { KTUtil } from "../../../../_metronic/_assets/js/components/util";
import DatePicker from "react-multi-date-picker";
import Icon from "react-multi-date-picker/components/icon";

const generateTimeCategories = () => {
  const start_time = moment("00:00", "HH:mm");
  const end_time = moment("24:00", "HH:mm");
  let time_options = [];
  while (start_time < end_time) {
    time_options.push(start_time.format("HH:mm"));
    //Add interval of 30 minutes
    start_time.add(30, "minutes");
  }
  return time_options;
};

const constructReservationByStatus = (data = [], status = "") => {
  if (isEmpty(data)) {
    return 0;
  }
  if (status === "all") {
    return data.reduce((acc, { count }) => acc + count, 0);
  }
  const return_val = data.filter((item) => item._id === status)[0];
  return !isEmpty(return_val) ? return_val.count : 0;
};

const generateWeekCategories = (date) => {
  const start_week = moment(date, DB_DATE_FORMAT).startOf("week");
  const end_week = moment(date, DB_DATE_FORMAT).endOf("week");
  const days_arr = [];
  let day = start_week;
  while (day <= end_week) {
    days_arr.push({
      date: moment(day).format(DB_DATE_FORMAT),
      day: day.format("ddd"),
      week: day.weekday(),
    });
    day = day.add(1, "d");
  }
  return days_arr;
};

export default function Page() {
  const params = useParams(),
    { user } = useSelector((state) => state.auth),
    { brand_url = "", store_id = "" } = params;
  return isAccessAllowed(user, { brand_url, store_id }) ? (
    <ReservationsDashboard user={user} {...params} />
  ) : (
    <Redirect to={"/error/error-v3"} />
  );
}
const TIME_CATEGORIES = generateTimeCategories();

const SalesByTime = ({ analytics = [] }) => {
  const { salesByTime = [] } = analytics?.[0] || {};

  const metrics = React.useMemo(() => {
    let series_data_sales = new Array(TIME_CATEGORIES.length).fill(0);
    let series_data_count = new Array(TIME_CATEGORIES.length).fill(0);

    salesByTime.forEach(({ _id = "", total_income = 0, total_sold = 0 }) => {
      const index = TIME_CATEGORIES.indexOf(_id);
      if (index !== -1) {
        series_data_sales[index] = total_income;
        series_data_count[index] = total_sold;
      }
    });

    return {
      series: [
        {
          name: "Sales",
          data: series_data_sales,
        },
        {
          name: "Customer #",
          data: series_data_count,
          type: "column",
        },
      ],
      options: {
        chart: {
          height: 350,
          type: "line",
          stacked: false,
          toolbar: {
            show: false,
          },
        },
        title: {
          text: "Sales/Customers by Time",
        },
        dataLabels: {
          enabled: false,
        },
        colors: GRAPH_COLORS,
        stroke: {
          width: [4, 4],
        },
        plotOptions: {
          bar: {
            columnWidth: "20%",
          },
        },
        xaxis: {
          categories: TIME_CATEGORIES,
          tickAmount: 12,
          labels: {
            formatter: (value) => {
              return moment(value, "HH:mm").format("hh:mm A");
            },
          },
        },
        yaxis: [
          {
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
            },
            title: {
              text: "Sales",
            },
          },
          {
            opposite: true,
            axisTicks: {
              show: true,
            },
            axisBorder: {
              show: true,
            },
            title: {
              text: "Customer #",
            },
          },
        ],
        tooltip: {
          y: {
            formatter: (val, { seriesIndex = -1 }) => {
              if (seriesIndex === 0) {
                return toPesoAmount(val);
              } else {
                return val + " pax";
              }
            },
          },
        },
        legend: {
          horizontalAlign: "left",
          offsetX: 40,
        },
      },
    };
  }, [salesByTime]);

  return (
    <Chart
      className={`${KTUtil.isMobileDevice() ? "" : "py-8"}`}
      options={metrics.options}
      series={metrics.series}
      type="line"
      height={350}
      width={KTUtil.isMobileDevice() ? 400 : 420}
    />
  );
};

const SalesByWeek = ({
  analytics = [],
  start_date = moment().format(DB_DATE_FORMAT),
}) => {
  const { salesByWeek = [] } = analytics?.[0] || {};
  const currentWeek = React.useMemo(
    () => generateWeekCategories(start_date),
    [start_date],
  );

  const metrics = React.useMemo(() => {
    const groupByName = groupBy(salesByWeek, "name");
    const series_data = Object.keys(groupByName).map((store) => {
      const store_data = Array(7).fill(0);
      groupByName[store].forEach(({ total_income, _id }) => {
        const week = _id?.week;
        if (week >= 1 && week <= 7) {
          store_data[week - 1] = total_income;
        }
      });
      return {
        name: store,
        data: store_data,
      };
    });

    return {
      series: !isEmpty(groupByName)
        ? series_data
        : [{ name: "", data: Array(7).fill(0) }],
      options: {
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        plotOptions: {
          bar: {
            horizontal: false,
          },
        },
        stroke: {
          show: true,
          width: 2,
          colors: ["transparent"],
        },
        title: {
          text: "Weekly Sales by Store",
          align: "left",
        },
        colors: GRAPH_COLORS,
        xaxis: {
          type: "datetime",
          categories: currentWeek.map(({ date }) => date),
        },
        dataLabels: {
          enabled: false,
        },
        fill: {
          opacity: 1,
        },
        tooltip: {
          y: {
            formatter: (val) => {
              return toPesoAmount(val);
            },
          },
        },
        legend: {
          position: "bottom",
          clusterGroupedSeriesOrientation: "vertical",
        },
      },
      grid: {
        borderColor: "#f1f1f1",
      },
    };
  }, [salesByWeek, currentWeek]);

  return (
    <Chart
      className={`${KTUtil.isMobileDevice() ? "" : "p-8"}`}
      options={metrics.options}
      series={metrics.series}
      type="bar"
      height={350}
      width={KTUtil.isMobileDevice() ? 400 : "100%"}
    />
  );
};

const SalesByPaymentMethod = ({ analytics = [] }) => {
  const { salesByPaymentMethod = [] } = analytics?.[0] || {};

  const metric = React.useMemo(() => {
    let is5k = false;

    const getPaymentData = (methodId) => {
      return salesByPaymentMethod
        .filter(({ _id }) => _id === methodId)
        .map(({ total }) => {
          if (!is5k) is5k = total >= 5000;
          return is5k
            ? Math.round((total / 1000 + Number.EPSILON) * 100) / 100
            : total;
        });
    };

    const gcashData = getPaymentData("gcash");
    const grabpayData = getPaymentData("grab_pay");
    const cardData = getPaymentData("card");

    return {
      series: [
        {
          name: "GCash",
          data: gcashData,
        },
        {
          name: "GrabPay",
          data: grabpayData,
        },
        {
          name: "Card",
          data: cardData,
        },
      ],
      options: {
        chart: {
          type: "bar",
          height: 350,
          stacked: true,
        },
        colors: GRAPH_COLORS,
        title: {
          text: "Sales by Payment Method",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            dataLabels: {
              total: {
                enabled: true,
                offsetX: 0,
                style: {
                  fontSize: "13px",
                  fontWeight: 900,
                },
              },
            },
          },
        },
        stroke: {
          width: 1,
          colors: ["#fff"],
        },
        xaxis: {
          categories: ["Sales"],
          labels: {
            formatter: function (val) {
              return val + (is5k ? "K" : "");
            },
          },
        },
        yaxis: {
          title: {
            text: undefined,
          },
        },
        tooltip: {
          y: {
            formatter: function (val) {
              return val + (is5k ? "K" : "");
            },
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: "bottom",
          horizontalAlign: "left",
          offsetX: 40,
        },
      },
    };
  }, [salesByPaymentMethod]);

  return (
    <Chart
      className={`${KTUtil.isMobileDevice() ? "" : "p-8"}`}
      options={metric.options}
      series={metric.series}
      type="bar"
      height={350}
      width={KTUtil.isMobileDevice() ? 400 : "100%"}
    />
  );
};

class ReservationsDashboard extends APIComponent {
  constructor(props) {
    super(props);
    const { brand_id = "" } = props;
    const default_range = generateWeekCategories(new Date());
    const start_date =
      default_range[0]?.date || moment().format(DB_DATE_FORMAT);
    const end_date =
      default_range[6]?.date || moment().add(1, "week").format(DB_DATE_FORMAT);
    const current_date = moment().format(DB_DATE_FORMAT);

    this.state = {
      ...this.state,
      store_name: "",
      isReservationAvailable: true,
      current_date,
      start_date,
      end_date,
      isFetchingAnalytics: false,
    };
    this.data_url = `brand/${brand_id}/reservations-analytics?start=${start_date}&end=${end_date}&current=${current_date}`;
    this.api = core("pu").get();

    this.generateDateRange = this.generateDateRange.bind(this);
  }

  async componentDidMount() {
    this._isMounted = true;
    await this.getData();
  }

  async componentDidUpdate(_, prevState) {
    const { start_date, end_date, current_date } = this.state;
    const prevCurrentDate = moment(prevState.current_date).format(
      DB_DATE_FORMAT,
    );
    const currCurrentDate = moment(current_date).format(DB_DATE_FORMAT);

    if (
      currCurrentDate !== prevCurrentDate ||
      start_date !== prevState.start_date ||
      end_date !== prevState.end_date
    ) {
      this.data_url = `brand/${this.props.brand_id}/reservations-analytics?start=${start_date}&end=${end_date}&current=${currCurrentDate}`;
      await this.getData();
    }
  }

  handleOnDataFetched = () => {
    this.setState({
      isFetchingAnalytics: false,
    });
  };

  handleOnDataFetchError = () => {
    this.setState({
      isFetchingAnalytics: false,
    });
  };

  generateDateRange(date = moment().format(DB_DATE_FORMAT)) {
    const dates = generateWeekCategories(date);
    return [dates[0].date, dates[6].date];
  }

  render() {
    const {
      isReservationAvailable,
      data = [],
      data_status = "",
      current_date = new Date(),
      isFetchingAnalytics = false,
      start_date = moment().format(DB_DATE_FORMAT),
      end_date = moment().add(6, "days").format(DB_DATE_FORMAT),
    } = this.state;
    const {
      today_snapshot = [],
      reservation_analytics = [],
      analytics = [],
      stores = [],
    } = data;

    return (
      <>
        {(data_status === "fetching" || data_status === "initial") &&
        !isFetchingAnalytics ? (
          <>
            <div className="d-flex justify-content-center align-items-center h-100">
              <PacmanWrapper removeBackgroundColor />
            </div>
          </>
        ) : (
          <MainContainer className={`${KTUtil.isMobileDevice() ? "p-0" : ""}`}>
            <Row className="d-flex justify-content-space-between align-items-center m-0 mb-3">
              <div className="d-flex flex-row justify-content-start align-items-center">
                <DatePicker
                  render={<Icon className="text-primary mb-4 mr-2" />}
                  value={current_date}
                  onChange={(date) => {
                    if (date) {
                      const dates = this.generateDateRange(
                        date.format(DB_DATE_FORMAT),
                      );
                      this.setState({
                        current_date: new Date(date),
                        start_date: dates[0],
                        end_date: dates[1],
                        isFetchingAnalytics: true,
                      });
                    }
                  }}
                />
                <HeaderText
                  title={moment(new Date(current_date)).format(
                    RESERVATION_DATE_HEADER_FORMAT,
                  )}
                />
              </div>
              <AvailablityContainer
                className={`d-flex align-items-center font-pickup-sm-1 justify-content-space-between px-3 ${
                  KTUtil.isMobileDevice() ? "w-100 " : ""
                }`}
              >
                <div className="d-flex flex-row">
                  <OnlineIndicator
                    color={isReservationAvailable ? "#00E33D" : "#FF0000"}
                  />
                  <span>
                    <b>
                      Availability{" "}
                      {isReservationAvailable ? "Enabled" : "Disabled"}{" "}
                    </b>
                  </span>
                </div>
                <a
                  style={{ textDecoration: "underline" }}
                  onClick={() =>
                    this.setState({
                      isReservationAvailable: !isReservationAvailable,
                    })
                  }
                >
                  Edit
                </a>
              </AvailablityContainer>
            </Row>
            <div
              className={`d-flex justify-content-space-between align-items-center flex-${
                KTUtil.isMobileDevice() ? "column w-100" : "row"
              }`}
            >
              <TotalReservations
                data={reservation_analytics}
                isLoading={isFetchingAnalytics}
              />
              <ReservationsDiv
                stores={stores}
                data={reservation_analytics}
                isLoading={isFetchingAnalytics}
              />
            </div>
            <Container
              className="d-flex align-items-center justify-content-center p-4 mt-5 p-4 font-weight-bolder text-center"
              style={{
                backgroundColor: "white",
                width: 300,
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              }}
            >
              {/* <span className="mx-4 font-weight-bold d-flex align-items-center justify-content-center"> */}
              Sales from {start_date} to {end_date}
            </Container>
            <div
              className={`d-flex justify-content-space-between align-items-center flex-${
                KTUtil.isMobileDevice() ? "column w-100" : "row"
              }`}
            >
              <Container className="d-flex flex-column w-100">
                <Row
                  className={`d-flex justify-content-space-between align-items-center m-0 ${
                    KTUtil.isMobileDevice() ? "py-6" : ""
                  }`}
                >
                  {isFetchingAnalytics && (
                    <div
                      className="d-flex w-100 justify-content-center align-items-center"
                      style={{ height: 350 }}
                    >
                      <PacmanLoading />
                    </div>
                  )}
                  {!isFetchingAnalytics && (
                    <>
                      <SalesByWeek
                        analytics={analytics}
                        start_date={start_date}
                      />
                      <SalesByTime analytics={analytics} />
                      <SalesByPaymentMethod analytics={analytics} />
                    </>
                  )}
                </Row>
              </Container>
            </div>

            <Row className="m-0">
              {!stores && (
                <Container
                  style={{ width: "100%" }}
                  className="d-flex justify-content-center mt-5"
                >
                  <PacmanLoading />
                </Container>
              )}
              {stores && (
                <ReservationSnapshots today_snapshot={today_snapshot} />
              )}
            </Row>
          </MainContainer>
        )}
      </>
    );
  }
}

const ReservationSnapshots = ({ today_snapshot = [] }) => {
  const { statusCount = [] } = today_snapshot?.[0] || {};
  const today_completed = constructReservationByStatus(
    statusCount,
    "completed",
  );
  const today_new = constructReservationByStatus(statusCount, "new");
  const options = {
    chart: {
      id: "statPie",
      type: "radialBar",
      sparkline: {
        enabled: true,
      },
    },
    plotOptions: {
      radialBar: {
        hollow: {
          margin: 0,
          size: "45%",
        },
        startAngle: -90,
        endAngle: 90,
        track: {
          background: "#4D0AF5",
          startAngle: -90,
          endAngle: 90,
        },
        offsetX: 20,
        offsetY: 0,
        dataLabels: {
          show: true,
          name: {
            show: true,
            offsetY: -10,
          },
          value: {
            show: true,
            offsetY: -50,
            fontWeight: 700,
            fontSize: 36,
          },
          total: {
            show: true,
            label: "Total",
            formatter: (w) => {
              return (
                w.globals.seriesTotals.reduce((a, b) => {
                  return a + b;
                }, 0) / w.globals.series.length
              );
            },
          },
        },
      },
    },
    grid: {
      padding: {
        top: -35,
        bottom: 20,
      },
    },
    fill: {
      opacity: 1,
      colors: ["#08DDB8"],
    },
  };

  return (
    <Container style={{ width: "100%" }} className="flex-column mt-5">
      <div className="d-flex flex-row ml-4 pt-3 font-size-h4 justify-content-space-between">
        <b>Today's Snapshot</b>
      </div>
      <div
        className={`d-flex flex-${
          KTUtil.isMobileDevice()
            ? "column justify-center"
            : "row justify-content-space-evenly pl-7"
        } pt-1 pb-3 `}
      >
        <Chart
          width={350}
          height={400}
          options={options}
          type="radialBar"
          series={[today_completed + today_new]}
        />
        <ChartLegendContainer
          completed_count={today_completed}
          new_count={today_new}
        />
      </div>
    </Container>
  );
};

const ChartLegendContainer = ({ completed_count = 0, new_count = 0 }) => {
  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center"
      style={{ flexBasis: "55%", height: "100%" }}
    >
      <div style={{ width: "70%" }}>
        <div className="d-flex flex-row">
          <Col className="d-flex text-uppercase font-weight-bolder">Status</Col>
          <Col className="d-flex text-uppercase font-weight-bolder justify-content-center">
            Reservations
          </Col>
        </div>
        <div className="d-flex flex-row mt-2">
          <Col className="d-flex font-weight-bolder">
            <div className="d-flex flex-row">
              <div
                className="d-flex bullet bullet-dot"
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "#65DABA",
                  margin: 5,
                  marginLeft: 0,
                }}
              />
              <span>New</span>
            </div>
          </Col>
          <Col className="d-flex font-weight-bolder justify-content-center">
            {new_count}
          </Col>
        </div>
        <div className="d-flex flex-row">
          <Col className="d-flex font-weight-bolder">
            <div className="d-flex flex-row">
              <div
                className="d-flex bullet bullet-dot"
                style={{
                  width: 10,
                  height: 10,
                  backgroundColor: "#4D0AF5",
                  margin: 5,
                  marginLeft: 0,
                }}
              />
              <span>Completed</span>
            </div>
          </Col>
          <Col className="d-flex font-weight-bolder justify-content-center">
            {completed_count}
          </Col>
        </div>
        <LegendDivider />
        <div className="d-flex flex-row">
          <Col className="d-flex font-weight-bolder">
            <div className="d-flex flex-row font-size-lg">
              <span>
                <b>Total</b>
              </span>
            </div>
          </Col>
          <Col className="d-flex text-uppercase font-weight-bolder justify-content-center">
            {completed_count + new_count}
          </Col>
        </div>
      </div>
    </div>
  );
};

const ReservationsDiv = ({ data = [], stores = [], isLoading = false }) => {
  const { reservationsByStore = [] } = data?.[0] || {};
  const reservations = stores.map(({ _id, name }) => {
    let count = 0;
    if (reservationsByStore.length > 0) {
      const store_data = reservationsByStore.filter(
        (data) => data.store_id === _id,
      )[0];
      count = isEmpty(store_data) ? 0 : store_data.count;
    }

    return { store_id: _id, name, count };
  });
  return (
    <Container
      className="flex-column"
      style={{
        width: KTUtil.isMobileDevice() ? "100%" : "60%",
        height: "100%",
      }}
    >
      <div className="d-flex flex-row pl-4 pt-3 font-size-h4">
        <b>Reservations</b>
      </div>{" "}
      {isLoading && (
        <div className="d-flex justify-content-center align-items-center font-weight-bolder h-100 py-10">
          Loading...
        </div>
      )}
      {!isLoading && (
        <Col md={12}>
          <div className="bw-reservations">
            <BWSection className="py-3">
              <BWSection.Content>
                <div className="reservations-count-div position-sticky bg-white ">
                  <nav className="position-sticky reservations-count-div-nav">
                    <ul
                      className="nav nav-pills flex-nowrap"
                      style={{ width: "100%" }}
                    >
                      {(reservations || []).map(
                        ({ store_id, name, count }, i) => {
                          return (
                            <li
                              className={`flex-grow-1 flex-shrink-0 nav-item category-menu-nav-item mx-5`}
                              key={`count-${i}`}
                            >
                              <Link
                                to={`/stores/${store_id}/reservations/home`}
                                className="text-dark-75 font-weight-bold font-size-sm"
                              >
                                <ReservationCountDiv className="d-flex flex-column align-items-center">
                                  <div className="d-flex flex-row font-weight-bolder display4 mb-0 d-block">
                                    {count}
                                  </div>
                                  <div className="d-flex flex-row">
                                    <div
                                      style={{
                                        maxWidth: data.length >= 4 ? 100 : 150,
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                      }}
                                    >
                                      {name}
                                    </div>
                                  </div>
                                </ReservationCountDiv>
                              </Link>
                            </li>
                          );
                        },
                      )}
                    </ul>
                  </nav>
                </div>
              </BWSection.Content>
            </BWSection>
          </div>
        </Col>
      )}
    </Container>
  );
};

const TotalReservations = ({ data = [], isLoading = false }) => {
  const { statusCount = [] } = data?.[0] || {};
  const total_count = constructReservationByStatus(statusCount, "all");
  const total_new = constructReservationByStatus(statusCount, "new");
  const total_cancelled = constructReservationByStatus(
    statusCount,
    "cancelled",
  );
  const total_complete = constructReservationByStatus(statusCount, "completed");
  return (
    <Container
      className={`flex-column m-0 ${KTUtil.isMobileDevice() ? "mb-3" : ""}`}
      style={{ width: KTUtil.isMobileDevice() ? "100%" : "39%" }}
    >
      <div className="d-flex flex-row pl-4 pt-3 font-size-h4">
        <b>Total Reservations</b>
      </div>
      {isLoading && (
        <div className="d-flex justify-content-center align-items-center font-weight-bolder h-100 py-10">
          Loading...
        </div>
      )}
      {!isLoading && (
        <div className="d-flex flex-row justify-content-space-evenly py-3">
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex font-weight-bolder display4 mb-0">
              {total_count}
            </div>
            <div className="d-flex">Reservations</div>
          </div>
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex font-weight-bolder display4 mb-0">
              {total_new}
            </div>
            <div className="d-flex">New</div>
          </div>
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex font-weight-bolder display4 mb-0">
              {total_cancelled}
            </div>
            <div className="d-flex">Cancelled</div>
          </div>
          <div className="d-flex flex-column align-items-center">
            <div className="d-flex font-weight-bolder display4 mb-0">
              {total_complete}
            </div>
            <div className="d-flex">Completed</div>
          </div>
        </div>
      )}
    </Container>
  );
};

export const Container = styled.div`
  display: flex;
  background: #ffffff 0% 0% no-repeat padding-box;
  /* box-shadow: 0px 3px 6px #00000029; */
  border-radius: 4px;
`;

export const MainContainer = styled.div`
  padding: 0 25px;
  display: flex;
  flex-direction: column;
`;

const LegendDivider = styled.div`
  height: 1px;
  background-color: #707070;
  margin: 20px 50px 10px 10px;
`;

const OnlineIndicator = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 18px;
  background-color: ${(props) => props.color};
  margin-right: 10px;
`;

const AvailablityContainer = styled(Container)`
  width: 300px;
  height: 40px;
  margin-top: -20px;
`;

const ReservationCountDiv = styled.div`
  &:focus,
  &:hover {
    color: #14c49b;
    transition: all 0.15s ease !default;
  }
`;
